import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { api } from '@/lib/api';
import BubbleCard from '@/components/BubbleCard';
import TouchRitual from '@/components/TouchRitual';
import { Link as LinkIcon, QrCode, At, Copy, ArrowRight, Sparkle as SparkleIcon, Users } from '@phosphor-icons/react';

const METHODS = [
  { key: 'link', icon: LinkIcon, label: 'Share Link' },
  { key: 'qr', icon: QrCode, label: 'QR Code' },
  { key: 'username', icon: At, label: 'To Username' },
  { key: 'nfc', icon: SparkleIcon, label: 'Touch Phones' },
  { key: 'circle', icon: Users, label: 'Circle Pass' },
];

const PassBubble = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [bubble, setBubble] = useState(null);
  const [method, setMethod] = useState('link');
  const [toUsername, setToUsername] = useState('');
  const [message, setMessage] = useState('');
  const [passToken, setPassToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ritualOpen, setRitualOpen] = useState(false);
  const [circleSize, setCircleSize] = useState(3);
  const [circleInfo, setCircleInfo] = useState(null);

  useEffect(() => {
    api.get(`/bubbles/${id}`).then(({ data }) => setBubble(data.bubble));
  }, [id]);

  // Poll circle status
  useEffect(() => {
    if (!passToken || method !== 'circle') return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/passes/${passToken}`);
        if (alive) setCircleInfo(data.pass);
      } catch {}
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [passToken, method]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { method, message };
      if (method === 'username') body.to_username = toUsername.trim();
      if (method === 'circle') body.circle_size = Math.max(2, Math.min(10, circleSize));
      const { data } = await api.post(`/bubbles/${id}/pass`, body);
      if (data.pass_token) {
        setPassToken(data.pass_token);
        if (method === 'nfc' || method === 'circle') setRitualOpen(true);
        toast.success(
          method === 'nfc' ? 'Bridge open — touch phones' :
          method === 'circle' ? `Circle of ${circleSize} opened` :
          'Share this with the lucky recipient'
        );
      } else {
        toast.success('Energy passed!');
        nav(`/bubble/${id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'pass failed');
    } finally { setBusy(false); }
  };

  const link = passToken ? `${window.location.origin}/claim/${passToken}` : '';

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success('Link copied');
  };

  if (!bubble) return <div className="text-center py-20 text-white/65">loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Pass it forward</h1>
        <p className="text-white/60 mt-2">Every transfer grows energy, velocity, and the movement.</p>
      </div>

      <div className="flex justify-center mb-8">
        <BubbleCard bubble={bubble} size="md" />
      </div>

      {!passToken ? (
        <div className="glass rounded-2xl p-6 space-y-5">
          <div>
            <span className="text-xs uppercase tracking-widest text-white/65">Method</span>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {METHODS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`pass-method-${m.key}`}
                  onClick={() => setMethod(m.key)}
                  className={`px-3 py-3 rounded-xl border text-sm font-bold flex flex-col items-center gap-1 transition ${
                    method === m.key
                      ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan'
                      : 'border-white/10 text-white/70 hover:bg-white/5'
                  }`}
                >
                  <m.icon size={18} weight="bold" />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {method === 'circle' && (
            <div className="grad-border p-4">
              <div className="text-[10px] uppercase tracking-widest text-bx-volt flex items-center gap-1">
                <Users size={12} weight="fill" /> circle pass — chain through {circleSize} phones
              </div>
              <div className="mt-3 flex items-center gap-2">
                {[2, 3, 5, 7, 10].map((n) => (
                  <button
                    key={n} data-testid={`circle-size-${n}`}
                    onClick={() => setCircleSize(n)}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
                      circleSize === n ? 'bg-bx-volt text-black' : 'glass-hi text-white/70 hover:bg-white/10'
                    }`}
                  >{n}</button>
                ))}
              </div>
              <div className="text-xs text-white/55 mt-3 italic">
                One link, {circleSize} touches. Each tap forwards the bubble to the next person. Schoolyard magic.
              </div>
            </div>
          )}

          {method === 'username' && (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Recipient username</span>
              <input
                data-testid="pass-to-username"
                value={toUsername} onChange={(e) => setToUsername(e.target.value)}
                placeholder="@founder"
                className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm"
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Personal message (optional)</span>
            <textarea
              data-testid="pass-message"
              value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} placeholder="Drop a word, story, or hype."
              className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm resize-none"
            />
          </label>

          <button data-testid="pass-submit" onClick={submit} disabled={busy} className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? 'Sending energy…' : <>
              {method === 'nfc' ? 'Open the touch bridge' : 'Send energy'} <ArrowRight size={16} weight="bold" />
            </>}
          </button>
        </div>
      ) : (
        <div className="glass rounded-2xl p-8 text-center space-y-5">
          <h3 className="font-display text-2xl font-bold">Your pass is ready</h3>
          {method === 'nfc' && (
            <button
              data-testid="reopen-ritual"
              onClick={() => setRitualOpen(true)}
              className="btn-volt px-5 py-3 rounded-full text-sm font-bold"
            >Reopen the touch ritual</button>
          )}
          {method === 'qr' && (
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-2xl">
                <QRCodeCanvas value={link} size={192} fgColor="#050505" bgColor="#ffffff" />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl p-3 max-w-lg mx-auto">
            <code data-testid="pass-link" className="flex-1 font-mono text-xs text-white/80 truncate text-left">{link}</code>
            <button data-testid="pass-copy" onClick={copy} className="btn-volt px-3 py-1.5 rounded-full text-xs flex items-center gap-1">
              <Copy size={12} weight="bold" /> Copy
            </button>
          </div>
          <Link to={`/bubble/${id}`} className="text-bx-cyan text-sm font-bold" data-testid="pass-done">Back to bubble →</Link>
        </div>
      )}

      <TouchRitual
        open={ritualOpen}
        link={link}
        bubble={bubble}
        passToken={passToken}
        circle={method === 'circle' ? circleInfo : null}
        onClose={() => setRitualOpen(false)}
      />
    </div>
  );
};

export default PassBubble;
